// Single source of truth for the 24 optional profile fields (Batch 3).
// Consumed by:
//   - Admin Create / Edit Member dialog (MemberFormFields)
//   - Member self-edit page (ProfilePage)
//   - Profile viewer readout (browse / liked / suite)
// Also mirrored in /app/mobile/src/data/extendedProfileFields.ts

export const SKIN_TONES = ['Putih', 'Kuning Langsat', 'Sawo Matang', 'Coklat', 'Gelap'];
export const HAIR_TYPES = ['Lurus', 'Bergelombang', 'Keriting', 'Ikal', 'Botak'];

// section keys drive the accordion UI. Field ORDER in the array = UI order.
export const EXTENDED_FIELDS = [
  // ── Data Fisik ──
  { key: 'height_cm',   section: 'physical', type: 'number', min: 100, max: 250, unit: 'cm',
    label_id: 'Tinggi Badan', label_en: 'Height' },
  { key: 'weight_kg',   section: 'physical', type: 'number', min: 30,  max: 200, unit: 'kg',
    label_id: 'Berat Badan', label_en: 'Weight' },
  { key: 'skin_tone',   section: 'physical', type: 'select', options: SKIN_TONES,
    label_id: 'Warna Kulit', label_en: 'Skin Tone' },
  { key: 'hair_type',   section: 'physical', type: 'select', options: HAIR_TYPES,
    label_id: 'Jenis Rambut', label_en: 'Hair Type' },

  // ── Keluarga & Ibadah ──
  { key: 'child_position',   section: 'family', type: 'number', min: 1, max: 20,
    label_id: 'Anak ke-', label_en: 'Child #' },
  { key: 'siblings_total',   section: 'family', type: 'number', min: 1, max: 20,
    label_id: 'Dari … Bersaudara', label_en: 'Of … siblings' },
  { key: 'routine_worship',   section: 'family', type: 'yesno',
    label_id: 'Ibadah Rutin?', label_en: 'Regular Worship?',
    hint_id: 'Ya/Tidak — bebas agama apa saja', hint_en: 'Yes/No — any religion' },
  { key: 'scripture_memorization', section: 'family', type: 'text', maxLength: 200,
    label_id: 'Hapalan Kitab Suci (jenis & jumlah)', label_en: 'Scripture Memorization (type & amount)',
    hint_id: 'Contoh: 5 juz Al-Quran, atau Mazmur bab 23, atau Sutra Hati', hint_en: 'e.g. 5 juz Quran, or Psalm 23, or Heart Sutra' },
  { key: 'target_marriage_year', section: 'family', type: 'number', min: 2026, max: 2050,
    label_id: 'Target Nikah (tahun)', label_en: 'Target Marriage Year' },
  { key: 'life_motto', section: 'family', type: 'textarea', maxLength: 240,
    label_id: 'Motto Hidup', label_en: 'Life Motto' },

  // ── Gaya Hidup & Kesehatan ──
  { key: 'smokes',        section: 'lifestyle', type: 'yesno',
    label_id: 'Merokok?', label_en: 'Smokes?' },
  { key: 'vapes',         section: 'lifestyle', type: 'yesno',
    label_id: 'Vape?', label_en: 'Vapes?' },
  { key: 'has_tattoo',    section: 'lifestyle', type: 'yesno',
    label_id: 'Bertato?', label_en: 'Has Tattoo?' },
  { key: 'medical_history', section: 'lifestyle', type: 'textarea', maxLength: 400,
    label_id: 'Riwayat Penyakit', label_en: 'Medical History',
    hint_id: 'Kosongkan jika tidak ada', hint_en: 'Leave blank if none' },

  // ── Preferensi Pasangan ──
  { key: 'partner_max_older',   section: 'preference', type: 'number', min: 0, max: 30,
    label_id: 'Max Pasangan Lebih Tua (thn)', label_en: 'Max Partner Older (yr)' },
  { key: 'partner_max_younger', section: 'preference', type: 'number', min: 0, max: 30,
    label_id: 'Max Pasangan Lebih Muda (thn)', label_en: 'Max Partner Younger (yr)' },
  { key: 'open_to_younger_partner', section: 'preference', type: 'yesno',
    label_id: 'Mau Pasangan Lebih Muda?', label_en: 'Open to Younger Partner?' },
  { key: 'require_same_religion',   section: 'preference', type: 'yesno',
    label_id: 'Pasangan Wajib Seagama?', label_en: 'Same Religion Required?' },
  { key: 'require_same_ethnicity',  section: 'preference', type: 'yesno',
    label_id: 'Pasangan Wajib Se-Ras/Suku?', label_en: 'Same Ethnicity Required?' },
  { key: 'ldr_ok', section: 'preference', type: 'yesno',
    label_id: 'LDR OK?', label_en: 'LDR OK?' },

  // ── Penilaian Diri (1-10) ──
  { key: 'busyness_level',      section: 'selfrating', type: 'slider', min: 1, max: 10,
    label_id: 'Tingkat Kesibukan', label_en: 'Busyness Level' },
  { key: 'commitment_level',    section: 'selfrating', type: 'slider', min: 1, max: 10,
    label_id: 'Komitmen Hubungan', label_en: 'Commitment Level' },
  { key: 'financial_condition', section: 'selfrating', type: 'slider', min: 1, max: 10,
    label_id: 'Kondisi Finansial', label_en: 'Financial Condition' },
  { key: 'self_quality',        section: 'selfrating', type: 'slider', min: 1, max: 10,
    label_id: 'Kualitas Diri', label_en: 'Self Quality' },
];

export const SECTIONS = [
  { key: 'physical',   label_id: 'Data Fisik',        label_en: 'Physical' },
  { key: 'family',     label_id: 'Keluarga & Ibadah', label_en: 'Family & Faith' },
  { key: 'lifestyle',  label_id: 'Gaya Hidup & Kesehatan', label_en: 'Lifestyle & Health' },
  { key: 'preference', label_id: 'Preferensi Pasangan', label_en: 'Partner Preferences' },
  { key: 'selfrating', label_id: 'Penilaian Diri (1-10)', label_en: 'Self-Rating (1-10)' },
];

export const EXTENDED_FIELD_KEYS = EXTENDED_FIELDS.map(f => f.key);
